/**
 * What a maneuver is, as a shape rather than a sentence.
 *
 * Valhalla numbers its maneuvers, and the number is the only part of an
 * instruction a reader takes in without reading it. Twenty-odd codes collapse
 * to twelve drawings: the differences this leaves out — a ramp from a fork, an
 * exit from a merge — are ones the sentence beside the icon already makes, and
 * twelve recognisable shapes beat twenty near-identical ones.
 *
 * Shared by the itinerary, which draws them with icons, and the PDF, which
 * draws them with lines. One mapping, so the screen and the file never
 * disagree about which way a turn goes.
 */
export type TurnShape =
  | 'start' | 'finish' | 'straight'
  | 'slight-left' | 'left' | 'sharp-left'
  | 'slight-right' | 'right' | 'sharp-right'
  | 'uturn' | 'roundabout' | 'ferry';

export function shapeOf(type: number): TurnShape {
  switch (type) {
    case 1: case 2: case 3: return 'start';
    case 4: case 5: case 6: return 'finish';
    case 9: case 18: case 23: case 38: return 'slight-right';
    case 10: case 20: return 'right';
    case 11: return 'sharp-right';
    case 12: case 13: return 'uturn';
    case 14: return 'sharp-left';
    case 15: case 21: return 'left';
    case 16: case 19: case 24: case 39: return 'slight-left';
    case 26: case 27: return 'roundabout';
    case 28: case 29: return 'ferry';
    default: return 'straight';
  }
}

/**
 * How far a single turn runs, in the unit the round is read in.
 *
 * Never feet or metres, however short the step is. A list of turns is read
 * down the left-hand numbers and across to the distance, and a column that
 * changes unit between rows — "36 ft, 794 ft, 0.3 mi" — has to be converted
 * in the head before two steps can be compared. Short steps take a second
 * decimal rather than a different unit.
 */
export function turnDistance(metres: number, units: 'metric' | 'imperial'): string {
  const value = units === 'imperial' ? metres / 1609.344 : metres / 1000;
  const unit = units === 'imperial' ? 'mi' : 'km';

  return `${value.toFixed(value < 0.1 ? 2 : 1)} ${unit}`;
}
